import { AdminNavigation } from "@/components/AdminNavigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BookOpen, FileDown, History, RefreshCw } from "lucide-react";
import {
  handbuchVersions,
  currentHandbuchVersion,
  currentHandbuchPdf,
  handbuchGeneratedAt,
  formatHandbuchDate,
} from "@/data/handbuchVersions";
import handbuchContent from "@/data/handbuchContent.json";

const HANDBOOK_PDF = currentHandbuchPdf;

interface Section {
  id: string;
  title: string;
  intro?: string;
  bullets?: string[];
  table?: { head: string[]; rows: string[][] };
}

const sections = handbuchContent.sections as Section[];


export default function AdminHandbuch() {
  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <BookOpen className="h-7 w-7 text-primary" />
              <h1 className="text-3xl font-bold">Handbuch</h1>
              <Badge variant="secondary">
                Version {currentHandbuchVersion.version} · Stand{" "}
                {formatHandbuchDate(currentHandbuchVersion.date)}
              </Badge>
            </div>
            <p className="text-muted-foreground">
              Funktionen der Homepage und technische Dokumentation der Programmierung – als
              Nachschlagewerk für Bedienung, Wartung, Audit und Übergabe.
            </p>
          </div>
          <Button asChild>
            <a href={HANDBOOK_PDF} download>
              <FileDown className="w-4 h-4 mr-2" /> Handbuch als PDF (v
              {currentHandbuchVersion.version})
            </a>
          </Button>
        </div>

        <Card className="mb-8" id="versionen">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <History className="w-5 h-5 text-primary" /> Versionierung &amp; Download-Historie
            </CardTitle>
            <CardDescription>
              Jede Fassung bleibt als unveränderliche PDF-Datei abrufbar – für Audit, Übergabe und
              Nachvollziehbarkeit der Änderungen.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {handbuchVersions.map((v) => (
              <div
                key={v.version}
                className="rounded-lg border p-4 space-y-2"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">Version {v.version}</span>
                  <Badge variant={v.status === "aktuell" ? "default" : "outline"}>
                    {v.status === "aktuell" ? "aktuell" : "archiviert"}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    Änderungsdatum: {formatHandbuchDate(v.date)} · {v.author}
                  </span>
                  <Button asChild variant="ghost" size="sm" className="ml-auto">
                    <a href={v.pdf} download>
                      <FileDown className="w-4 h-4 mr-2" /> PDF v{v.version}
                    </a>
                  </Button>
                </div>
                <p className="text-sm">{v.summary}</p>
                <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                  {v.changes.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </div>
            ))}
            <div className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground flex items-start gap-2">
              <RefreshCw className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
              <span>
                Automatisiert: Online-Handbuch und PDF stammen aus derselben Quelle. Wird der Inhalt
                geändert, entsteht beim nächsten Speichern bzw. bei der nächsten Veröffentlichung
                automatisch eine neue Version mit Änderungsdatum – das PDF wird neu erzeugt und
                zusätzlich unveränderlich archiviert. Letzte Prüfung:{" "}
                {formatHandbuchDate(handbuchGeneratedAt)}.
              </span>
            </div>

          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Inhalt</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="grid gap-1 sm:grid-cols-2 text-sm">
              {sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="text-primary hover:underline">
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>


        <div className="space-y-6">
          {sections.map((s) => (
            <Card key={s.id} id={s.id} className="scroll-mt-24">
              <CardHeader>
                <CardTitle className="text-xl">{s.title}</CardTitle>
                {s.intro && <CardDescription>{s.intro}</CardDescription>}
              </CardHeader>
              <CardContent className="space-y-4">
                {s.bullets && (
                  <ul className="list-disc pl-5 space-y-2 text-sm text-muted-foreground">
                    {s.bullets.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                )}
                {s.table && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-muted-foreground border-b">
                          <th className="py-2 pr-4 font-medium">{s.table.head[0]}</th>
                          <th className="py-2 font-medium">{s.table.head[1]}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {s.table.rows.map(([a, b]) => (
                          <tr key={a} className="border-b last:border-0 align-top">
                            <td className="py-2 pr-4 font-medium sm:whitespace-nowrap">{a}</td>
                            <td className="py-2 text-muted-foreground">{b}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        <p className="mt-8 text-xs text-muted-foreground">
          © 2017-2026 EAP Abutments®. Alle Rechte vorbehalten. Internes Dokument – nicht zur
          Veröffentlichung bestimmt.
        </p>
      </div>
    </div>
  );
}
